import React, { Suspense, lazy } from 'react';
import { HiArrowNarrowRight, HiOutlineDocumentDownload } from 'react-icons/hi';
import { FaGithub, FaLinkedinIn } from 'react-icons/fa';
import { Link } from 'react-scroll';
import { motion, useReducedMotion } from 'framer-motion';
import CanvasBoundary from './three/CanvasBoundary';
import useInView from '../hooks/useInView';

const HeroScene = lazy(() => import('./three/HeroScene'));

const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const item = {
    hidden: { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const Home = () => {
    const reducedMotion = useReducedMotion();
    const [sceneRef, sceneInView] = useInView('0px');

    return (
        <section name='home' className='relative w-full min-h-screen flex items-center pt-[80px] overflow-hidden'>
            <div className='absolute inset-0 bg-grid pointer-events-none' aria-hidden="true" />

            <div className='relative max-w-[1160px] w-full mx-auto px-6 sm:px-8 grid lg:grid-cols-[1.2fr_1fr] items-center gap-6 py-12'>
                {/* Text column */}
                <motion.div variants={container} initial="hidden" animate="show" className='relative z-10'>
                    <motion.p variants={item} className='inline-flex items-center gap-2 text-sm text-slate-400 mb-6'>
                        <span className='h-2 w-2 rounded-full bg-emerald-400' aria-hidden="true" />
                        Available for new opportunities
                    </motion.p>

                    <motion.h1 variants={item} className='text-4xl sm:text-6xl font-bold text-slate-50 tracking-tight leading-[1.08]'>
                        Sharvan Samala
                    </motion.h1>
                    <motion.p variants={item} className='mt-3 text-lg sm:text-2xl font-medium text-[#64ffda]'>
                        Software Developer · Backend, Cloud & AI Systems
                    </motion.p>
                    <motion.p variants={item} className='text-slate-400 mt-6 max-w-[580px] text-base sm:text-lg leading-relaxed'>
                        I build scalable cloud and backend systems, specializing in <span className="text-slate-200">Python</span>,{' '}
                        <span className="text-slate-200">LLMs & AI systems</span>, <span className="text-slate-200">Java / Spring Boot</span>,
                        and <span className="text-slate-200">AWS</span>. I engineer reliable, high-performance distributed systems and AI applications.
                    </motion.p>

                    <motion.div variants={item} className='flex flex-wrap items-center gap-3 mt-8'>
                        <Link to="work" smooth={true} duration={600} offset={-80} className='btn-primary group'>
                            View My Work
                            <HiArrowNarrowRight className='group-hover:translate-x-0.5 duration-200' />
                        </Link>
                        <a
                            href="https://drive.google.com/file/d/16WEqsstKK7R0R365Vvy2sjN-Ipb9I7i_/view?usp=sharing"
                            target="_blank"
                            rel="noreferrer noopener"
                            className='btn-ghost'
                        >
                            <HiOutlineDocumentDownload className="text-base" /> Resume
                        </a>
                    </motion.div>

                    <motion.div variants={item} className='flex items-center gap-5 mt-10 text-slate-500'>
                        <a href="https://github.com/Sharvansamala" target="_blank" rel="noreferrer noopener" aria-label="GitHub Profile" className='hover:text-slate-200 transition-colors'>
                            <FaGithub size={20} />
                        </a>
                        <a href="https://www.linkedin.com/in/sharvansamala" target="_blank" rel="noreferrer noopener" aria-label="LinkedIn Profile" className='hover:text-slate-200 transition-colors'>
                            <FaLinkedinIn size={20} />
                        </a>
                        <span className='h-px w-10 bg-slate-700' aria-hidden="true" />
                        <a href="mailto:samalasharvan@gmail.com" className='text-sm hover:text-slate-200 transition-colors'>
                            samalasharvan@gmail.com
                        </a>
                    </motion.div>
                </motion.div>

                {/* 3D network visual */}
                <motion.div
                    ref={sceneRef}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.2, delay: 0.3 }}
                    className='relative h-[320px] sm:h-[420px] lg:h-[560px]'
                    aria-hidden="true"
                >
                    <CanvasBoundary>
                        <Suspense fallback={null}>
                            <HeroScene active={sceneInView} reducedMotion={reducedMotion} />
                        </Suspense>
                    </CanvasBoundary>
                </motion.div>
            </div>
        </section>
    );
};

export default Home;
